import { useEffect, useMemo, useRef, useState } from 'react'
import { SY_BOARD as boardData, PLAYER_COLOR_HEX } from '@mdev/common'
import type { Board, GameState } from '@mdev/common'
import { SY } from '../theme'

const board = boardData as Board

const MAP_WIDTH = 2570
const MAP_HEIGHT = 1926
const MIN_SCALE = 0.15
const MAX_SCALE = 4

const PAWN_RADIUS = 52
const PAWN_HALO = 8
const REVEAL_RING_BASE = 70
const PAWN_ANIM_MS = 700

type StationTier = 'taxi' | 'bus' | 'underground'

const STATION_TIER_FILL: Record<StationTier, string> = {
  taxi: '#FCD34D',
  bus: '#60A5FA',
  underground: '#F87171',
}

const stationTierById = ( () => {
  const map = new Map<number, StationTier>()
  for ( const s of board.stations ) map.set( s.id, 'taxi' )
  for ( const e of board.edges ) {
    if ( e.transport === 'bus' ) {
      if ( map.get( e.from ) === 'taxi' ) map.set( e.from, 'bus' )
      if ( map.get( e.to ) === 'taxi' ) map.set( e.to, 'bus' )
    } else if ( e.transport === 'underground' ) {
      map.set( e.from, 'underground' )
      map.set( e.to, 'underground' )
    }
  }
  return map
} )()

interface View {
  scale: number
  tx: number
  ty: number
}

interface PawnAnimation {
  fromX: number
  fromY: number
  toX: number
  toY: number
  startedAt: number
}

interface Props {
  gameState?: GameState
  /** Initial focus point in map coords (defaults to map center). */
  focusOn?: { x: number; y: number }
  /** Multiplier on the fit-to-screen scale for the initial view (default 1.8 = "zoomed in"). */
  initialZoom?: number
  /**
   * The Mr. X player's OWN secret position, drawn only on his device. The
   * public `gameState` never contains Mr. X's station, so without this his
   * pawn wouldn't render on his own map.
   */
  mrxSelf?: { station: number; avatar: string }
  /**
   * When false, draw only the bare (undimmed) map — no station dots, numbers,
   * pawns or reveal marker — so a player can pan/zoom and study the board.
   * Defaults to true.
   */
  showMarkers?: boolean
}

/** Mr. X's pawn colour (he has no `PlayerColor` — he's "black"). */
const MRX_PAWN_COLOR = '#111827'

export function InteractiveBoardCanvas( {
  gameState,
  focusOn,
  initialZoom = 1.8,
  mrxSelf,
  showMarkers = true,
}: Props ) {
  const containerRef = useRef<HTMLDivElement>( null )
  const canvasRef = useRef<HTMLCanvasElement>( null )
  const mapImgRef = useRef<HTMLImageElement | null>( null )
  const [ size, setSize ] = useState( { w: 0, h: 0 } )
  const [ mapLoaded, setMapLoaded ] = useState( false )
  const [ view, setView ] = useState<View>( { scale: 1, tx: 0, ty: 0 } )
  const initializedRef = useRef( false )

  const stationById = useMemo( () => new Map( board.stations.map( ( s ) => [ s.id, s ] ) ), [] )

  useEffect( () => {
    const img = new Image()
    img.src = '/map.png'
    img.onload = () => {
      mapImgRef.current = img
      setMapLoaded( true )
    }
  }, [] )

  useEffect( () => {
    const el = containerRef.current
    if ( !el ) return
    const ro = new ResizeObserver( ( entries ) => {
      const r = entries[ 0 ].contentRect
      setSize( { w: r.width, h: r.height } )
    } )
    ro.observe( el )
    return () => ro.disconnect()
  }, [] )

  useEffect( () => {
    if ( initializedRef.current ) return
    if ( size.w === 0 || size.h === 0 ) return
    const fit = Math.min( size.w / MAP_WIDTH, size.h / MAP_HEIGHT )
    const scale = fit * initialZoom
    const center = focusOn ?? { x: MAP_WIDTH / 2, y: MAP_HEIGHT / 2 }
    const tx = size.w / 2 - center.x * scale
    const ty = size.h / 2 - center.y * scale
    setView( clampView( { scale, tx, ty }, size ) )
    initializedRef.current = true
  }, [ size, focusOn, initialZoom ] )

  const animationsRef = useRef<Map<string, PawnAnimation>>( new Map() )
  const lastStationsRef = useRef<Map<string, number>>( new Map() )

  useEffect( () => {
    if ( !gameState ) return
    const now = performance.now()
    for ( const d of gameState.detectives ) {
      if ( d.station == null ) {
        lastStationsRef.current.delete( d.uid )
        continue
      }
      const prev = lastStationsRef.current.get( d.uid )
      if ( prev != null && prev !== d.station ) {
        const toS = stationById.get( d.station )
        if ( toS ) {
          const existing = animationsRef.current.get( d.uid )
          let fromX: number
          let fromY: number
          if ( existing && now - existing.startedAt < PAWN_ANIM_MS ) {
            const progress = Math.min( 1, ( now - existing.startedAt ) / PAWN_ANIM_MS )
            const eased = 1 - Math.pow( 1 - progress, 3 )
            fromX = existing.fromX + ( existing.toX - existing.fromX ) * eased
            fromY = existing.fromY + ( existing.toY - existing.fromY ) * eased
          } else {
            const prevS = stationById.get( prev )
            fromX = prevS?.x ?? toS.x
            fromY = prevS?.y ?? toS.y
          }
          animationsRef.current.set( d.uid, { fromX, fromY, toX: toS.x, toY: toS.y, startedAt: now } )
        }
      }
      lastStationsRef.current.set( d.uid, d.station )
    }
  }, [ gameState, stationById ] )

  const pointersRef = useRef<Map<number, { x: number; y: number }>>( new Map() )
  const pinchStartRef = useRef<{ distance: number; mapX: number; mapY: number; scale: number } | null>( null )
  const panStartRef = useRef<{ pointerX: number; pointerY: number; tx: number; ty: number } | null>( null )

  function pointFromEvent( ev: { clientX: number; clientY: number } ) {
    const rect = containerRef.current!.getBoundingClientRect()
    return { x: ev.clientX - rect.left, y: ev.clientY - rect.top }
  }

  function handlePointerDown( ev: React.PointerEvent<HTMLDivElement> ) {
    const p = pointFromEvent( ev )
    pointersRef.current.set( ev.pointerId, p )
    ;( ev.target as Element ).setPointerCapture?.( ev.pointerId )

    if ( pointersRef.current.size >= 2 ) {
      const pts = Array.from( pointersRef.current.values() ).slice( 0, 2 )
      const distance = Math.hypot( pts[ 0 ].x - pts[ 1 ].x, pts[ 0 ].y - pts[ 1 ].y )
      const midX = ( pts[ 0 ].x + pts[ 1 ].x ) / 2
      const midY = ( pts[ 0 ].y + pts[ 1 ].y ) / 2
      pinchStartRef.current = {
        distance,
        scale: view.scale,
        mapX: ( midX - view.tx ) / view.scale,
        mapY: ( midY - view.ty ) / view.scale,
      }
      panStartRef.current = null
    } else {
      panStartRef.current = { pointerX: p.x, pointerY: p.y, tx: view.tx, ty: view.ty }
    }
  }

  function handlePointerMove( ev: React.PointerEvent<HTMLDivElement> ) {
    if ( !pointersRef.current.has( ev.pointerId ) ) return
    pointersRef.current.set( ev.pointerId, pointFromEvent( ev ) )

    if ( pointersRef.current.size >= 2 && pinchStartRef.current ) {
      const pts = Array.from( pointersRef.current.values() ).slice( 0, 2 )
      const distance = Math.hypot( pts[ 0 ].x - pts[ 1 ].x, pts[ 0 ].y - pts[ 1 ].y )
      const midX = ( pts[ 0 ].x + pts[ 1 ].x ) / 2
      const midY = ( pts[ 0 ].y + pts[ 1 ].y ) / 2
      const newScale = pinchStartRef.current.scale * ( distance / pinchStartRef.current.distance )
      const tx = midX - pinchStartRef.current.mapX * newScale
      const ty = midY - pinchStartRef.current.mapY * newScale
      setView( clampView( { scale: newScale, tx, ty }, size ) )
    } else if ( pointersRef.current.size === 1 && panStartRef.current ) {
      const p = pointersRef.current.values().next().value!
      const dx = p.x - panStartRef.current.pointerX
      const dy = p.y - panStartRef.current.pointerY
      setView( clampView( { scale: view.scale, tx: panStartRef.current.tx + dx, ty: panStartRef.current.ty + dy }, size ) )
    }
  }

  function handlePointerEnd( ev: React.PointerEvent<HTMLDivElement> ) {
    pointersRef.current.delete( ev.pointerId )
    if ( pointersRef.current.size < 2 ) pinchStartRef.current = null
    if ( pointersRef.current.size === 0 ) {
      panStartRef.current = null
    } else if ( pointersRef.current.size === 1 ) {
      const p = pointersRef.current.values().next().value!
      panStartRef.current = { pointerX: p.x, pointerY: p.y, tx: view.tx, ty: view.ty }
    }
  }

  function handleWheel( ev: React.WheelEvent<HTMLDivElement> ) {
    const p = pointFromEvent( ev )
    const factor = Math.exp( -ev.deltaY * 0.0015 )
    const newScale = view.scale * factor
    const mapX = ( p.x - view.tx ) / view.scale
    const mapY = ( p.y - view.ty ) / view.scale
    const tx = p.x - mapX * newScale
    const ty = p.y - mapY * newScale
    setView( clampView( { scale: newScale, tx, ty }, size ) )
  }

  useEffect( () => {
    const canvas = canvasRef.current
    if ( !canvas ) return
    let rafId = 0
    const dpr = window.devicePixelRatio || 1

    function frame( t: number ) {
      if ( size.w === 0 || size.h === 0 ) {
        rafId = requestAnimationFrame( frame )
        return
      }
      const wantW = Math.round( size.w * dpr )
      const wantH = Math.round( size.h * dpr )
      if ( canvas!.width !== wantW || canvas!.height !== wantH ) {
        canvas!.width = wantW
        canvas!.height = wantH
        canvas!.style.width = `${ size.w }px`
        canvas!.style.height = `${ size.h }px`
      }
      const ctx = canvas!.getContext( '2d' )
      if ( !ctx ) {
        rafId = requestAnimationFrame( frame )
        return
      }

      ctx.setTransform( 1, 0, 0, 1, 0, 0 )
      ctx.fillStyle = '#0B0F1A'
      ctx.fillRect( 0, 0, canvas!.width, canvas!.height )

      const s = view.scale * dpr
      ctx.setTransform( s, 0, 0, s, view.tx * dpr, view.ty * dpr )

      if ( mapLoaded && mapImgRef.current ) {
        ctx.drawImage( mapImgRef.current, 0, 0, MAP_WIDTH, MAP_HEIGHT )
        // Dim the map so markers pop — but only when markers are shown.
        if ( showMarkers ) {
          ctx.fillStyle = 'rgba(11, 15, 26, 0.45)'
          ctx.fillRect( 0, 0, MAP_WIDTH, MAP_HEIGHT )
        }
      }

      if ( !showMarkers ) {
        rafId = requestAnimationFrame( frame )
        return
      }

      const detectiveStations = new Set( ( gameState?.detectives ?? [] ).filter( ( d ) => d.station != null ).map( ( d ) => d.station! ) )

      ctx.textAlign = 'center'
      ctx.textBaseline = 'middle'
      for ( const sn of board.stations ) {
        if ( detectiveStations.has( sn.id ) ) continue
        const tier = stationTierById.get( sn.id ) ?? 'taxi'
        if ( sn.isFerry ) {
          ctx.beginPath()
          ctx.arc( sn.x, sn.y, 24, 0, Math.PI * 2 )
          ctx.strokeStyle = '#67E8F9'
          ctx.lineWidth = 3
          ctx.stroke()
        }
        ctx.beginPath()
        ctx.arc( sn.x, sn.y, 20, 0, Math.PI * 2 )
        ctx.fillStyle = STATION_TIER_FILL[ tier ]
        ctx.fill()
        ctx.lineWidth = 2
        ctx.strokeStyle = '#0B0F1A'
        ctx.stroke()
        ctx.fillStyle = '#0B0F1A'
        ctx.font = 'bold 18px system-ui, -apple-system, sans-serif'
        ctx.fillText( String( sn.id ), sn.x, sn.y )
      }

      if ( gameState?.mrx.lastRevealedStation != null ) {
        const sn = stationById.get( gameState.mrx.lastRevealedStation )
        if ( sn ) drawRevealMarker( ctx, sn.x, sn.y, t )
      }

      if ( gameState ) {
        for ( const d of gameState.detectives ) {
          if ( d.station == null ) continue
          const sn = stationById.get( d.station )
          if ( !sn ) continue
          const awaiting = gameState.awaitingUids.includes( d.uid )
          let x = sn.x
          let y = sn.y
          const anim = animationsRef.current.get( d.uid )
          if ( anim ) {
            const elapsed = t - anim.startedAt
            if ( elapsed >= PAWN_ANIM_MS ) {
              animationsRef.current.delete( d.uid )
            } else {
              const progress = Math.max( 0, elapsed / PAWN_ANIM_MS )
              const eased = 1 - Math.pow( 1 - progress, 3 )
              x = anim.fromX + ( anim.toX - anim.fromX ) * eased
              y = anim.fromY + ( anim.toY - anim.fromY ) * eased
            }
          }
          drawPawn( ctx, x, y, PLAYER_COLOR_HEX[ d.color ], d.avatar, awaiting, t )
        }
      }

      // Mr. X's own pawn — drawn only on his device (his station is private and
      // never in `gameState`). Pulses when it's his turn to move.
      if ( mrxSelf ) {
        const sn = stationById.get( mrxSelf.station )
        if ( sn ) {
          const awaiting = gameState
            ? gameState.awaitingUids.includes( gameState.mrx.uid )
            : false
          drawPawn( ctx, sn.x, sn.y, MRX_PAWN_COLOR, mrxSelf.avatar, awaiting, t )
        }
      }

      rafId = requestAnimationFrame( frame )
    }
    rafId = requestAnimationFrame( frame )
    return () => cancelAnimationFrame( rafId )
  }, [ size, view, mapLoaded, gameState, stationById, mrxSelf, showMarkers ] )

  return (
    <div
      ref={ containerRef }
      onPointerDown={ handlePointerDown }
      onPointerMove={ handlePointerMove }
      onPointerUp={ handlePointerEnd }
      onPointerCancel={ handlePointerEnd }
      onPointerLeave={ handlePointerEnd }
      onWheel={ handleWheel }
      style={ {
        width: '100%',
        height: '100%',
        overflow: 'hidden',
        touchAction: 'none',
        background: SY.pageBg,
        position: 'relative',
        userSelect: 'none',
      } }
    >
      <canvas
        ref={ canvasRef }
        style={ { display: 'block' } }
      />
    </div>
  )
}

function clampView( v: View, size: { w: number; h: number } ): View {
  const scale = Math.max( MIN_SCALE, Math.min( MAX_SCALE, v.scale ) )
  const half = 0.4
  const minTx = size.w * half - MAP_WIDTH * scale
  const maxTx = size.w * ( 1 - half )
  const minTy = size.h * half - MAP_HEIGHT * scale
  const maxTy = size.h * ( 1 - half )
  return {
    scale,
    tx: Math.max( minTx, Math.min( maxTx, v.tx ) ),
    ty: Math.max( minTy, Math.min( maxTy, v.ty ) ),
  }
}

function drawRevealMarker( ctx: CanvasRenderingContext2D, x: number, y: number, t: number ) {
  const phase = ( t / 1500 ) % 1
  const ringR = REVEAL_RING_BASE + phase * 60
  const ringAlpha = Math.max( 0, 1 - phase )

  ctx.save()
  ctx.beginPath()
  ctx.arc( x, y, ringR, 0, Math.PI * 2 )
  ctx.strokeStyle = `rgba(250, 204, 21, ${ ringAlpha * 0.6 })`
  ctx.lineWidth = 6
  ctx.stroke()

  ctx.beginPath()
  ctx.arc( x, y, REVEAL_RING_BASE, 0, Math.PI * 2 )
  ctx.strokeStyle = '#FACC15'
  ctx.lineWidth = 6
  ctx.setLineDash( [ 14, 10 ] )
  ctx.lineDashOffset = -t / 60
  ctx.shadowColor = '#FACC15'
  ctx.shadowBlur = 30
  ctx.stroke()
  ctx.setLineDash( [] )
  ctx.shadowBlur = 0

  ctx.beginPath()
  ctx.arc( x, y, 30, 0, Math.PI * 2 )
  ctx.fillStyle = 'rgba(250, 204, 21, 0.95)'
  ctx.fill()
  ctx.lineWidth = 4
  ctx.strokeStyle = '#0B0F1A'
  ctx.stroke()
  ctx.fillStyle = '#0B0F1A'
  ctx.font = 'bold 38px system-ui, -apple-system, sans-serif'
  ctx.textAlign = 'center'
  ctx.textBaseline = 'middle'
  ctx.fillText( '?', x, y + 2 )
  ctx.restore()
}

function drawPawn( ctx: CanvasRenderingContext2D, x: number, y: number, color: string, avatar: string, awaiting: boolean, t: number ) {
  ctx.save()

  if ( awaiting ) {
    const phase = ( t / 1100 ) % 1
    const pulseR = PAWN_RADIUS + 6 + phase * 40
    const pulseAlpha = Math.max( 0, 1 - phase )
    ctx.beginPath()
    ctx.arc( x, y, pulseR, 0, Math.PI * 2 )
    ctx.strokeStyle = `rgba(252, 211, 77, ${ pulseAlpha * 0.8 })`
    ctx.lineWidth = 6
    ctx.stroke()
  }

  ctx.beginPath()
  ctx.arc( x, y + 4, PAWN_RADIUS + PAWN_HALO, 0, Math.PI * 2 )
  ctx.fillStyle = 'rgba(0, 0, 0, 0.45)'
  ctx.fill()

  ctx.beginPath()
  ctx.arc( x, y, PAWN_RADIUS + PAWN_HALO, 0, Math.PI * 2 )
  ctx.fillStyle = '#FFFFFF'
  ctx.fill()

  ctx.beginPath()
  ctx.arc( x, y, PAWN_RADIUS, 0, Math.PI * 2 )
  ctx.fillStyle = color
  ctx.fill()

  if ( awaiting ) {
    ctx.lineWidth = 6
    ctx.strokeStyle = '#FCD34D'
    ctx.shadowColor = '#FCD34D'
    ctx.shadowBlur = 24
    ctx.stroke()
    ctx.shadowBlur = 0
  }

  ctx.fillStyle = '#FFFFFF'
  ctx.font = 'bold 56px system-ui, -apple-system, sans-serif'
  ctx.textAlign = 'center'
  ctx.textBaseline = 'middle'
  ctx.fillText( avatar, x, y + 4 )

  ctx.restore()
}
